import { DEFAULT_EMAIL_DELIVERABILITY_SETTINGS, RiskyEmailPolicy } from "@signalist/shared-types";

/** The five workspace-wide email-deliverability settings on Contacts & Enrichment (PREC-4261). */
export interface EmailDeliverabilityValues {
	emailVerificationEnabled: boolean;
	riskyEmailPolicy: RiskyEmailPolicy;
	riskyEmailPauseAbove4Percent: boolean;
	bounceRepairEnabled: boolean;
	bounceRepairReviewOnCompanyChange: boolean;
}

const DELIVERABILITY_KEYS: (keyof EmailDeliverabilityValues)[] = [
	"emailVerificationEnabled",
	"riskyEmailPolicy",
	"riskyEmailPauseAbove4Percent",
	"bounceRepairEnabled",
	"bounceRepairReviewOnCompanyChange",
];

/** The loaded values, with the API's defaults standing in for a key the response did not carry. */
export const deliverabilityValuesOf = (
	settings: Partial<EmailDeliverabilityValues> | null | undefined
): EmailDeliverabilityValues => ({
	emailVerificationEnabled:
		settings?.emailVerificationEnabled ??
		DEFAULT_EMAIL_DELIVERABILITY_SETTINGS.emailVerificationEnabled,
	riskyEmailPolicy:
		settings?.riskyEmailPolicy ?? DEFAULT_EMAIL_DELIVERABILITY_SETTINGS.riskyEmailPolicy,
	riskyEmailPauseAbove4Percent:
		settings?.riskyEmailPauseAbove4Percent ??
		DEFAULT_EMAIL_DELIVERABILITY_SETTINGS.riskyEmailPauseAbove4Percent,
	bounceRepairEnabled:
		settings?.bounceRepairEnabled ?? DEFAULT_EMAIL_DELIVERABILITY_SETTINGS.bounceRepairEnabled,
	bounceRepairReviewOnCompanyChange:
		settings?.bounceRepairReviewOnCompanyChange ??
		DEFAULT_EMAIL_DELIVERABILITY_SETTINGS.bounceRepairReviewOnCompanyChange,
});

/**
 * The deliverability fields the user changed, and only those. A settings page writes an unset field
 * only once it was edited (root CLAUDE.md, "A control names what it will do"): saving Auto-Archive
 * must not create configuration rows for the cards that are still hidden behind their slice's
 * constant, and a hidden card cannot be edited, so nothing is written for it.
 */
export const changedDeliverabilityFields = (
	loaded: EmailDeliverabilityValues,
	current: EmailDeliverabilityValues
): Partial<EmailDeliverabilityValues> =>
	DELIVERABILITY_KEYS.reduce<Partial<EmailDeliverabilityValues>>(
		(changed, key) =>
			loaded[key] === current[key] ? changed : { ...changed, [key]: current[key] },
		{}
	);
